import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

/**
 * Service Worker message type for notification click
 */
type NotificationClickMessage = {
	type: 'NOTIFICATION_CLICK'
	url: string
	eventId?: string
	eventType?: string
}

/**
 * Hook to handle notification clicks from service worker
 * This enables navigation when clicking notifications in PWA mode
 *
 * Must be used inside a Router context
 */
export function useNotificationClickHandler() {
	const navigate = useNavigate()
	const isInitialized = useRef(false)

	useEffect(() => {
		// Prevent double initialization in strict mode
		if (isInitialized.current) return
		isInitialized.current = true

		// Check if service worker is supported
		if (!('serviceWorker' in navigator)) {
			console.log('[Notification] Service Worker not supported')
			return
		}

		const handleServiceWorkerMessage = (
			event: MessageEvent<NotificationClickMessage>,
		) => {
			if (event.data?.type === 'NOTIFICATION_CLICK') {
				const { url, eventId, eventType } = event.data
				console.log('[Notification] Click received:', {
					url,
					eventId,
					eventType,
				})

				if (url) {
					try {
						// Parse the URL to get the pathname
						const urlObj = new URL(url, window.location.origin)
						const pathname = urlObj.pathname + urlObj.search + urlObj.hash

						// Use React Router navigation for internal URLs
						if (urlObj.origin === window.location.origin) {
							navigate(pathname)
						} else {
							// External URL - open in new tab
							window.open(url, '_blank')
						}
					} catch (error) {
						console.error('[Notification] Error parsing URL:', error)
						// Fallback to direct navigation
						window.location.href = url
					}
				}
			}
		}

		// Add message listener for service worker messages
		navigator.serviceWorker.addEventListener(
			'message',
			handleServiceWorkerMessage,
		)

		console.log('[Notification] Click handler initialized')

		return () => {
			navigator.serviceWorker.removeEventListener(
				'message',
				handleServiceWorkerMessage,
			)
			isInitialized.current = false
		}
	}, [navigate])
}
